import React, { Component } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { obfuscatedCoordinates } from '../../util/maps';
import { Heading, Map } from '../../components';

import css from './ListingPage.module.css';

// Requests show a 1-mile circle, never a pin. It's centred on the listing's public
// geolocation, which is already rounded to 2 decimals (~1 km) when the request is
// created, so the home is always inside the circle and the circle reveals nothing new.
export const REQUEST_AREA_RADIUS_METERS = 1609;

/**
 * Map props for a listing's location section.
 *
 * @param {Object} params
 * @param {string} params.listingType
 * @param {Object} params.geolocation public geolocation
 * @param {Object} params.mapsConfig config.maps
 * @param {string} params.address public address text
 * @param {string} [params.cacheKey]
 * @returns {Object} props for Map
 */
export const mapPropsFor = ({
  listingType,
  geolocation,
  mapsConfig,
  address,
  cacheKey,
  exactLocation,
}) => {
  // The homeowner viewing their own request: an exact pin at their home.
  if (exactLocation) {
    return {
      address,
      center: exactLocation,
      mapsConfig: { ...mapsConfig, fuzzy: { ...mapsConfig.fuzzy, enabled: false } },
    };
  }
  if (listingType === 'request') {
    return {
      obfuscatedCenter: geolocation,
      mapsConfig: {
        ...mapsConfig,
        fuzzy: {
          ...mapsConfig.fuzzy,
          enabled: true,
          offset: REQUEST_AREA_RADIUS_METERS,
          defaultZoomLevel: 12,
        },
      },
    };
  }
  return mapsConfig.fuzzy.enabled
    ? { obfuscatedCenter: obfuscatedCoordinates(geolocation, mapsConfig.fuzzy.offset, cacheKey) }
    : { address, center: geolocation };
};

/**
 * The address line shown under "Location" on a request: the full address (with unit)
 * for the homeowner and pros who've responded, the street only for a picked pro who
 * hasn't yet, nothing for anyone else. Comes from /api/request-details.
 *
 * @param {Object|null} details request details
 * @returns {{line: string, approximate: boolean}|null}
 */
export const locationText = details => {
  const full = details?.address?.address;
  if (full) {
    return {
      line: [full, details.address.building].filter(Boolean).join(', '),
      approximate: false,
    };
  }
  if (details?.approximateAddress) {
    return { line: details.approximateAddress, approximate: true };
  }
  return null;
};

/**
 * The SectionMapMaybe component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.latlng} [props.geolocation] - The geolocation
 * @param {propTypes.uuid} props.listingId - The listing id
 * @param {propTypes.latlng} [props.exactLocation] - The homeowner's own exact point (their request only)
 * @param {Object} [props.requestDetails] - Request details, for the address line (requests only)
 * @returns {JSX.Element} section map maybe component
 */
class SectionMapMaybe extends Component {
  constructor(props) {
    super(props);
    this.state = { isStatic: true };
  }

  render() {
    const {
      className,
      rootClassName,
      geolocation,
      publicData,
      listingId,
      mapsConfig,
      exactLocation,
      requestDetails,
    } = this.props;
    const addressText = locationText(requestDetails);

    if (!geolocation) {
      return null;
    }

    const address = publicData && publicData.location ? publicData.location.address : '';
    const classes = classNames(rootClassName || css.sectionMap, className);
    const cacheKey = listingId ? `${listingId.uuid}_${geolocation.lat}_${geolocation.lng}` : null;

    const mapProps = mapPropsFor({
      listingType: publicData?.listingType,
      geolocation,
      mapsConfig,
      address,
      cacheKey,
      exactLocation,
    });
    const map = <Map {...mapProps} useStaticMap={this.state.isStatic} />;

    return (
      <section className={classes} id="listing-location">
        <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
          <FormattedMessage id="ListingPage.locationTitle" />
        </Heading>
        {addressText ? (
          <p className={css.locationAddress}>
            {addressText.line}
            {addressText.approximate ? (
              <span className={css.locationAddressNote}>
                <FormattedMessage id="RequestPrivateDetails.unlockAddress" />
              </span>
            ) : null}
          </p>
        ) : null}
        {this.state.isStatic ? (
          <button
            className={css.map}
            onClick={() => {
              this.setState({ isStatic: false });
            }}
          >
            {map}
          </button>
        ) : (
          <div className={css.map}>{map}</div>
        )}
      </section>
    );
  }
}

export default SectionMapMaybe;
